import React, { useState, useEffect, useRef } from 'react';
import useUserContext from '../common/UserContext';
import Processing from '../common/Processing';
import MessageTag from '../common/MessageTag';
import Strings from '../common/Strings';

import { selectOptions } from '../common/Forms';

import { apiGet, getAccountsApiUrl } from '../common/AppApi';
import './Dashboard.css';

import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default function Dashboard() {
	let { user } = useUserContext();
	let [showSpinner, setSpinner] = useState(false);
	let [message, setMessage] = useState();

	let [groups, setGroups] = useState([]);
	let [programs, setPrograms] = useState([]);
	let [agencies, setAgencies] = useState([{ programUuid: '', agencyUuid: '', agencyName: '' }]);
	let [fundingSources, setFundingSources] = useState([{ programUuid: '', fundingId: '', fundingName: '' }]);

	let [displayData, setDisplayData] = useState(null);
	let [programFundingData, setProgramFundingData] = useState(null);
	let [programRedemptionData, setProgramRedemptionData] = useState(null);
	let [programRedemptionCountData, setProgramRedemptionCountData] = useState(null);
	let [programIssuanceData, setProgramIssuanceData] = useState(null);
	let [programIssuanceCountData, setProgramIssuanceCountData] = useState(null);
	let [programRecipientRedemptionCountData, setProgramRecipientRedemptionCountData] = useState(null);
	let [merchantData, setMerchantData] = useState(null);
	let [topMerchantRedemptionsData, setTopMerchantRedemptionsData] = useState(null);
	let [programIncentiveData, setProgramIncentiveData] = useState(null);

	let [dateRanges, setDateRanges] = useState({
		dateRange1: '30', dateRange2: '30', dateRange3: '30', dateRange4: '30', dateRange5: '30',
		dateRange6: '30', dateRange7: '30', dateRange8: '30'
	});

	// --- NEW: Chart/Table toggles (keeps your current layout + CSS classes) ---
	const [viewMode, setViewMode] = useState({
		programFundingPie: 'chart',
		issuancePie: 'chart',
		redemptionPie: 'chart',
		redemptionsBar: 'chart',
		redemptionsCountBar: 'chart',
		recipientRedemptionsCountBar: 'chart',
		issuancesBar: 'chart',
		issuanceCountBar: 'chart',
		incentivesBar: 'chart',
		merchantParticipationPie: 'chart',
		topMerchantsBar: 'chart',
	});

	const setMode = (key, mode) => {
		setViewMode((prev) => ({ ...prev, [key]: mode }));
	};

	const IconChart = ({ size = 16 }) => (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			aria-hidden="true"
			focusable="false"
		>
			<path d="M4 19V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M4 19H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M8 17V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M12 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
			<path d="M16 17V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);

	const IconTable = ({ size = 16 }) => (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			fill="none"
			aria-hidden="true"
			focusable="false"
		>
			<rect x="4" y="6" width="16" height="12" rx="1.5" stroke="currentColor" strokeWidth="2" />
			<path d="M4 10H20" stroke="currentColor" strokeWidth="2" />
			<path d="M9 6V18" stroke="currentColor" strokeWidth="2" />
			<path d="M15 6V18" stroke="currentColor" strokeWidth="2" />
		</svg>
	);

	const ToggleButtons = ({ modeKey, className }) => (
		<div className={className ? className : ''} style={{ display: 'inline-flex', gap: 6, margin: '.5em' }}>
			<button
				type="button"
				className={'btn btn-sm ' + (viewMode[modeKey] === 'chart' ? 'btn-secondary' : 'btn-outline-secondary')}
				onClick={() => setMode(modeKey, 'chart')}
				aria-label="Show chart"
				title="Show chart"
			>
				<IconChart />
			</button>

			<button
				type="button"
				className={'btn btn-sm ' + (viewMode[modeKey] === 'table' ? 'btn-secondary' : 'btn-outline-secondary')}
				onClick={() => setMode(modeKey, 'table')}
				aria-label="Show table"
				title="Show table"
			>
				<IconTable />
			</button>
		</div>
	);

	const LY_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	const monthIndexFromYYYYMMDD = (s) => {
		// expects "YYYY-MM-DD" (like "2025-01-01")
		if (!s || s.length < 7) return null;
		const idx = parseInt(s.slice(5, 7), 10) - 1;
		return (idx >= 0 && idx < 12) ? idx : null;
	};

	useEffect(() => {
		let url = getAccountsApiUrl() + '/dash';
		apiGet(url).then((resp) => {
			setGroups(resp);

			let previousProgram = null;
			let progs = [{ programUuid: '', programName: 'Select...' }];
			resp.forEach((item) => {
				if (item.programUuid != previousProgram) {
					previousProgram = item.programUuid;
					progs.push({
						programUuid: item.programUuid,
						programName: item.programName
					});
				}
			});
			setPrograms(progs);
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		});
	}, []);

	let [searchForm, setSearchForm] = useState({ programUuid: '', agencyUuid: '', fundingId: '', dateRange: 'YTD' });
	let handleFieldChange = (event) => {
		const { id, value } = event.target;
		setSearchForm({ ...searchForm, [id]: value });
	};

	let handleDateRangeChange = (event) => {
		const { id, value } = event.target;
		setDateRanges({ ...dateRanges, [id]: value });
	};

	let getProgramName = (id) => {
		for (var i = 0; i < programs.length; i++) {
			if (programs[i].programUuid == id)
				return programs[i].programName;
		}
		return null;
	};
	let getAgencyName = (id) => {
		for (var i = 0; i < agencies.length; i++) {
			if (agencies[i].agencyUuid == id)
				return agencies[i].agencyName;
		}
		return null;
	};
	let getFundingName = (id) => {
		let theItem = groups.find((item) => {
			return item.fundingId == id;
		});
		return (theItem && theItem.fundingName) ? theItem.fundingName : '';
	};

	let programName = useRef(null);
	let currentProgramName = () => {
		return programName.current ? programName.current : 'Program';
	};
	let agencyName = useRef(null);
	let currentAgencyName = () => {
		return agencyName.current ? agencyName.current : 'Agency';
	};
	let fundingName = useRef(null);
	let currentFundingName = () => {
		return fundingName.current ? fundingName.current : null;
	};
	const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

	const formatChartLabel = (trxDate, rangeType) => {
		if (rangeType !== 'LY') return trxDate;
		const monthIndex = parseInt(trxDate.substring(5, 7), 10) - 1;
		return MONTHS[monthIndex];
	};

	useEffect(() => {
		let agenciesClone = [{ programUuid: '', agencyUuid: '', agencyName: '' }];
		let fundingClone = [{ programUuid: '', fundingId: '', fundingName: '' }];

		if (searchForm.programUuid) {
			let prevAgencyUuid = null, prevFundingId = null;

			for (var i = 0; i < groups.length; i++) {
				let item = groups[i];
				if (item.programUuid == searchForm.programUuid) {
					if (item.agencyUuid && item.agencyUuid != prevAgencyUuid) {
						prevAgencyUuid = item.agencyUuid;
						agenciesClone.push({
							agencyName: item.agencyName,
							agencyUuid: item.agencyUuid,
							programUuid: item.programUuid
						});
					}
					if (item.fundingId && item.fundingId != prevFundingId) {
						prevFundingId = item.fundingId;
						fundingClone.push({
							fundingId: item.fundingId,
							fundingName: item.fundingName,
							programUuid: item.programUuid
						});
					}
				}
			}
		}

		setAgencies(agenciesClone);
		setFundingSources(fundingClone);
		setSearchForm({ ...searchForm, agencyUuid: '', fundingId: '' });

		if (searchForm.programUuid) {
			programName.current = getProgramName(searchForm.programUuid);
		} else {
			programName.current = null;
		}
	}, [searchForm.programUuid]);

	useEffect(() => {
		if (searchForm.agencyUuid) {
			agencyName.current = getAgencyName(searchForm.agencyUuid);
		} else {
			agencyName.current = null;
		}
	}, [searchForm.agencyUuid]);

	useEffect(() => {
		if (searchForm.fundingId) {
			fundingName.current = getFundingName(searchForm.fundingId);
		} else {
			fundingName.current = null;
		}
	}, [searchForm.fundingId]);

	let agencyFunding = () => {
		setSpinner(true);
		let url = getAccountsApiUrl() + '/dash/agency-funding/' + searchForm.agencyUuid + '?dateRange=' + searchForm.dateRange;
		apiGet(url).then((resp) => {
			setProgramFundingData(resp);
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let programFunding = () => {
		setSpinner(true);
		let url = getAccountsApiUrl() + '/dash/program-funding/' + searchForm.programUuid + '?dateRange=' + searchForm.dateRange;
		apiGet(url).then((resp) => {
			setProgramFundingData(resp);
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let buildQueryParams = (dataType) => {
		let queryParams = [];

		if (searchForm.agencyUuid)
			queryParams.push('agencyUuid=' + searchForm.agencyUuid);
		if (searchForm.fundingId)
			queryParams.push('fundingId=' + searchForm.fundingId);

		if (queryParams.length == 0) {
			if (searchForm.programUuid)
				queryParams.push('programUuid=' + searchForm.programUuid);
		}

		if (dataType)
			queryParams.push('dataType=' + dataType);

		return queryParams.join('&');
	};

	let getMerchantData = (dateType, saveState) => {
		setSpinner(true);
		let url = getAccountsApiUrl() + '/dash/merchant-data/' + searchForm.programUuid;
		if (dateType)
			url += '?dateRange=' + dateType;
		apiGet(url).then((resp) => {
			saveState(resp);
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let getTopMerchantsRedemptionData = (dateType, saveState) => {
		setSpinner(true);
		let url = getAccountsApiUrl() + '/dash/top-merchant-redemptions' + '?' + buildQueryParams();
		if (dateType)
			url += '&dateRange=' + dateType;
		apiGet(url).then((resp) => {
			console.log('TOP MERCHANTS API RESPONSE:', resp);
			saveState(resp);
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let getChartData = (dataType, dateType, saveState) => {
		setSpinner(true);
		let url = getAccountsApiUrl() + '/dash/chart-data?' + buildQueryParams(dataType);
		if (dateType)
			url += '&dateRange=' + dateType;
		apiGet(url).then((resp) => {
			if (dataType === 2) {
		console.log('PROGRAM REDEMPTION API RESPONSE:', resp);
	}

			saveState(resp);
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	let programRedemptions = () => {
		getChartData(2, dateRanges.dateRange1, setProgramRedemptionData);
	};
	let programRedemptionsCount = () => {
		getChartData(1, dateRanges.dateRange2, setProgramRedemptionCountData);
	};
	let programIssuances = () => {
		getChartData(3, dateRanges.dateRange3, setProgramIssuanceData);
	};
	let programRecipientRedemptionsCount = () => {
		getChartData(4, dateRanges.dateRange4, setProgramRecipientRedemptionCountData);
	};

	// ---------------------------
	// Data change -> draw charts (ONLY if mode is chart)
	// ---------------------------
	useEffect(() => {
		if (programFundingData && viewMode.programFundingPie === 'chart') doProgramFundingChart();
	}, [programFundingData, viewMode.programFundingPie]);

	useEffect(() => {
		if (programRedemptionData && viewMode.redemptionsBar === 'chart') doRedemptionsChart();
	}, [programRedemptionData, viewMode.redemptionsBar]);

	useEffect(() => {
		if (programRedemptionCountData && viewMode.redemptionsCountBar === 'chart') doRedemptionsCountChart();
	}, [programRedemptionCountData, viewMode.redemptionsCountBar]);

	useEffect(() => {
		if (programIssuanceData && viewMode.issuancesBar === 'chart') doIssuancesChart();
	}, [programIssuanceData, viewMode.issuancesBar]);

	useEffect(() => {
		if (programRecipientRedemptionCountData && viewMode.recipientRedemptionsCountBar === 'chart') doRecipientRedemptionsCountChart();
	}, [programRecipientRedemptionCountData, viewMode.recipientRedemptionsCountBar]);

	useEffect(() => {
		if (merchantData && viewMode.merchantParticipationPie === 'chart') doMerchantParticipationChart();
	}, [merchantData, viewMode.merchantParticipationPie]);

	useEffect(() => {
		if (topMerchantRedemptionsData && viewMode.topMerchantsBar === 'chart') doTopMerchantRedemptionsChart();
	}, [topMerchantRedemptionsData, viewMode.topMerchantsBar]);

	useEffect(() => {
		if (programIssuanceCountData && viewMode.issuanceCountBar === 'chart') doIssuanceCountChart();
	}, [programIssuanceCountData, viewMode.issuanceCountBar]);

	useEffect(() => {
		if (programIncentiveData && viewMode.incentivesBar === 'chart') doIncentivesChart();
	}, [programIncentiveData, viewMode.incentivesBar]);

	useEffect(() => {
		if (displayData && viewMode.issuancePie === 'chart') doProgramIssuanceChart();
		if (displayData && viewMode.redemptionPie === 'chart') doRedemptionChart();
	}, [displayData, viewMode.issuancePie, viewMode.redemptionPie]);

	// ---------------------------
	// Date changes -> refetch data (same behavior as you had)
	// ---------------------------
	useEffect(() => {
		if (programRedemptionData) {
			if (searchForm.agencyUuid || searchForm.programUuid || searchForm.fundingId) {
				programRedemptions();
			}
		}
	}, [dateRanges.dateRange1]);

	useEffect(() => {
		if (programRedemptionCountData) {
			if (searchForm.agencyUuid || searchForm.programUuid || searchForm.fundingId) {
				programRedemptionsCount();
			}
		}
	}, [dateRanges.dateRange2]);

	useEffect(() => {
		if (programIssuanceData) {
			if (searchForm.agencyUuid || searchForm.programUuid || searchForm.fundingId) {
				programIssuances();
			}
		}
	}, [dateRanges.dateRange3]);

	useEffect(() => {
		if (programRecipientRedemptionCountData) {
			if (searchForm.agencyUuid || searchForm.programUuid || searchForm.fundingId) {
				programRecipientRedemptionsCount();
			}
		}
	}, [dateRanges.dateRange4]);

	useEffect(() => {
		if (merchantData) {
			if (searchForm.programUuid) {
				getMerchantData(dateRanges.dateRange5, setMerchantData);
			}
		}
	}, [dateRanges.dateRange5]);

	useEffect(() => {
		if (topMerchantRedemptionsData) {
			if (searchForm.programUuid) {
				getTopMerchantsRedemptionData(dateRanges.dateRange6, setTopMerchantRedemptionsData);
			}
		}
	}, [dateRanges.dateRange6]);

	useEffect(() => {
		if (programIssuanceCountData) {
			if (searchForm.programUuid) {
				getChartData(5, dateRanges.dateRange7, setProgramIssuanceCountData);
			}
		}
	}, [dateRanges.dateRange7]);

	useEffect(() => {
		if (programIncentiveData) {
			if (searchForm.programUuid) {
				getChartData(6, dateRanges.dateRange8, setProgramIncentiveData);
			}
		}
	}, [dateRanges.dateRange8]);

	useEffect(() => {
		if (dateRanges.initial) {
			programRedemptions();
			programRedemptionsCount();
			programIssuances();
			programRecipientRedemptionsCount();
			getMerchantData(dateRanges.dateRange5, setMerchantData);
			getTopMerchantsRedemptionData(dateRanges.dateRange6, setTopMerchantRedemptionsData);
			getChartData(5, dateRanges.dateRange7, setProgramIssuanceCountData);
			getChartData(6, dateRanges.dateRange8, setProgramIncentiveData);
			setDateRanges({ ...dateRanges, initial: false });
		}
	}, [dateRanges]);

	let handleSubmit = (e) => {
		console.log("In the function");
		e.preventDefault();
		setSpinner(true);
		setMessage(null);

		let queryParams = [];

		if (searchForm.agencyUuid)
			queryParams.push('agencyUuid=' + searchForm.agencyUuid);
		if (searchForm.fundingId)
			queryParams.push('fundingId=' + searchForm.fundingId);
		if (searchForm.programUuid)
			queryParams.push('programUuid=' + searchForm.programUuid);
		queryParams.push('dateRange=' + searchForm.dateRange);

		let url = getAccountsApiUrl() + '/dash/snapshot?' + queryParams.join('&');
		console.log(url);
		apiGet(url).then((resp) => {
			console.log("here");
			console.log(resp);
			if (resp.funding || resp.issued || resp.redeemed) {
				setDisplayData(resp);
				if (searchForm.agencyUuid) {
					agencyFunding();
				} else if (searchForm.programUuid) {
					programFunding();
				}

				setProgramRedemptionData(null);
				setProgramRedemptionCountData(null);
				setProgramIssuanceData(null);
				setProgramRecipientRedemptionCountData(null);
				setMerchantData(null);
				setTopMerchantRedemptionsData(null);
				setProgramIssuanceCountData(null);
				setProgramIncentiveData(null);

				setDateRanges({
					dateRange1: searchForm.dateRange, dateRange2: searchForm.dateRange, dateRange3: searchForm.dateRange,
					dateRange4: searchForm.dateRange, dateRange5: searchForm.dateRange,
					dateRange6: searchForm.dateRange, dateRange7: searchForm.dateRange,
					dateRange8: searchForm.dateRange, initial: true
				});
			} else {
				setMessage({ text: 'No benefit data available.', type: 'warning' });

				const charts = [
					fundingChart,
					redemptionChart,
					programFundingChart,
					redemptionsChart,
					issuancesChart,
					redemptionsCountChart,
					recipientRedemptionsCountChart,
					merchantParticipationChart,
					topMerchantRedemptionsChart,
					issuanceCountChart,
					incentivesChart,
				];
				charts.forEach((item) => {
					if (item && item.current)
						item.current.destroy();
				});

				const dataStores = [
					setDisplayData,
					setProgramFundingData,
					setProgramRedemptionData,
					setProgramIssuanceData,
					setProgramRedemptionCountData,
					setProgramRecipientRedemptionCountData,
					setMerchantData,
					setTopMerchantRedemptionsData,
					setProgramIssuanceCountData,
					setProgramIncentiveData,
				];
				console.log('Redemption Data');
				console.log(programRedemptionData);
				dataStores.forEach((item) => {
					item(null);
				});
			}
		}).catch((err) => {
			console.error(err);
			let messageObject = { text: Strings.error.general, type: 'warning' };
			if (err.privilegeError) {
				messageObject.text = <>{err.message}</>;
				messageObject.type = 'danger';
			}
			setMessage(messageObject);
		}).finally(() => setSpinner(false));
	};

	// ---------------------------
	// Table render helpers
	// ---------------------------
	const money = (n) => {
		if (n === null || n === undefined || isNaN(Number(n))) return '';
		return '$ ' + Number(n).toFixed(2);
	};

	const TableTrxAmount = ({ data, dateRangeId, amountKey = 'amount', dateKey = 'trxDate', labelAmount = 'Amount' }) => {
		if (!data || !data.length) return null;

		let rows = [];
		if (dateRangeId === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item[dateKey]);
				if (idx !== null) byMonth[idx] = Number(item[amountKey]) || 0;
			});
			rows = byMonth.map((amt, idx) => (
				<tr key={idx}>
					<th>{LY_MONTHS[idx]}</th>
					<td>{money(amt)}</td>
				</tr>
			));
		} else {
			rows = data.map((item, idx) => (
				<tr key={idx}>
					<th>{item[dateKey]}</th>
					<td>{money(item[amountKey])}</td>
				</tr>
			));
		}

		return (
			<>
				<br />
				<table className="table table-sm micro">
					<thead>
						<tr>
							<th>Date</th>
							<th>{labelAmount}</th>
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</>
		);
	};

	const TableTrxCount = ({ data, dateRangeId, countKey = 'count', dateKey = 'trxDate', labelCount = 'Count' }) => {
		if (!data || !data.length) return null;

		let rows = [];
		if (dateRangeId === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item[dateKey]);
				if (idx !== null) byMonth[idx] = Number(item[countKey]) || 0;
			});
			rows = byMonth.map((cnt, idx) => (
				<tr key={idx}>
					<th>{LY_MONTHS[idx]}</th>
					<td>{cnt}</td>
				</tr>
			));
		} else {
			rows = data.map((item, idx) => (
				<tr key={idx}>
					<th>{item[dateKey]}</th>
					<td>{item[countKey]}</td>
				</tr>
			));
		}

		return (
			<>
				<br />
				<table className="table table-sm micro">
					<thead>
						<tr>
							<th>Date</th>
							<th>{labelCount}</th>
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</>
		);
	};

	const TableTopMerchants = ({ data }) => {
		if (!data || !data.length) return null;
		const rows = data.map((item, idx) => (
			<tr key={idx}>
				<th>{item.dataName}</th>
				<td>{money(item.amount)}</td>
			</tr>
		));

		return (
			<>
				<br />
				<table className="table table-sm micro">
					<thead>
						<tr>
							<th>Merchant</th>
							<th>Amount</th>
						</tr>
					</thead>
					<tbody>{rows}</tbody>
				</table>
			</>
		);
	};

	let DisplayIssuance = () => {
		if (displayData) {
			return <><br />
				<table className='table table-sm micro'>
					<tbody>
						<tr>
							<th>Funding</th><td>$ {displayData.funding.toFixed(2)}</td><td></td>
						</tr>
						<tr>
							<th>Issued</th><td>$ {displayData.issued.toFixed(2)}</td><td>{displayData.issuedPct.toFixed(1)}%</td>
						</tr>
						<tr>
							<th>Expunged</th><td>$ {displayData.expunged.toFixed(2)}</td><td>{displayData.expungedPct.toFixed(1)}%</td>
						</tr>
						<tr>
							<th>Not Issued</th><td>$ {displayData.notIssued.toFixed(2)}</td><td>{displayData.notIssuedPct.toFixed(1)}%</td>
						</tr>
					</tbody>
				</table>
			</>;
		} else {
			return null;
		}
	};
	let DisplayRedemption = () => {
		if (displayData) {
			return <><br />
				<table className='table table-sm micro'>
					<tbody>
						<tr>
							<th>Issued</th><td>$ {displayData.issued.toFixed(2)}</td><td></td>
						</tr>
						<tr>
							<th>Redeemed</th><td>$ {displayData.redeemed.toFixed(2)}</td><td>{displayData.redemptionPct.toFixed(1)}%</td>
						</tr>
						<tr>
							<th>Not Redeemed</th><td>$ {displayData.notRedeemed.toFixed(2)}</td><td>{displayData.notRedeemedPct.toFixed(1)}%</td>
						</tr>
					</tbody>
				</table>
			</>;
		} else {
			return null;
		}
	};
	let DisplayAllocation = () => {
		if (programFundingData && programFundingData.length) {
			let rows = programFundingData.map((item, index) => {
				return <tr key={index}><th>{getFundingName(item.dataId || item.fundingId)}</th><td>${item.amount.toFixed(2)}</td><td>{item.percentage.toFixed(1)}%</td></tr>;
			});
			return <><br />
				<table className='table table-sm micro'>
					<tbody>
						{rows}
					</tbody>
				</table>
			</>;
		} else {
			return null;
		}
	};
	let DisplayMerchantParticipation = () => {
		if (merchantData && merchantData.merchantCount) {
			return <><br />
				<table className='table table-sm micro'>
					<tbody>
						<tr>
							<th>Enrolled Merchants</th><td>{merchantData.merchantCount.count}</td><td></td>
						</tr>
						<tr>
							<th>Merchants with a Redemption</th><td>{merchantData.merchantRedemptionCount.count}</td><td>{merchantData.merchantRedemptionCount.percentage.toFixed(1)}%</td>
						</tr>
						<tr>
							<th>Merchants without a Redemption</th><td>{merchantData.merchantNoRedemptionCount.count}</td><td>{merchantData.merchantNoRedemptionCount.percentage.toFixed(1)}%</td>
						</tr>
					</tbody>
				</table>
			</>;
		} else {
			return null;
		}
	};

	const namedColors = {
		red: 'rgb(255, 99, 132, .9)',
		blue: 'rgb(54, 162, 235, .9)',
		green: 'rgb(75, 192, 192, .9)',
		purple: 'rgb(145, 85, 225, .9)',
		orange: 'rgb(255, 150, 64, .9)',
		yellow: 'rgb(245, 205, 86, .9)',
		grey: 'rgb(185, 185, 195, .9)',
	};
	const backgroundColors = [
		namedColors.blue,
		namedColors.red,
		namedColors.green,
		namedColors.orange,
		namedColors.purple,
		namedColors.yellow,
		namedColors.grey,
	];

	const animationConfig = { duration: 600 };
	const tooltipCallbacks = {
		label: (context) => {
			return context.label + ': ' + context.parsed.toFixed(2);
		}
	};
	const pieOptionsConfig = { borderWidth: 1 };

	let fundingChart = useRef(null);
	let doProgramIssuanceChart = () => {
		let chartTitle = ['Issuance', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current)
			chartTitle.push(fundingName.current);
		const pieConfig = {
			type: 'pie',
			data: {
				labels: ['Issued ' + displayData.issuedPct + '%', 'Not Issued ' + displayData.notIssuedPct + '%', 'Expunged ' + displayData.expungedPct + '%'],
				datasets: [{
					data: [displayData.issued, displayData.notIssued,displayData.expunged],
					backgroundColor: backgroundColors,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: tooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				datasets: { pie: pieOptionsConfig }
			}
		};

		if (fundingChart.current) fundingChart.current.destroy();
		const ctx = document.getElementById('fundingChart');
		if (ctx) fundingChart.current = new Chart(ctx, pieConfig);
	};

	let redemptionChart = useRef(null);
	let doRedemptionChart = () => {
		let chartTitle = ['Redemption', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current)
			chartTitle.push(fundingName.current);
		const pieConfig = {
			type: 'pie',
			data: {
				labels: ['Redeemed ' + displayData.redemptionPct + '%', 'Not Redeemed ' + displayData.notRedeemedPct + '%'],
				datasets: [{
					data: [displayData.redeemed, displayData.notRedeemed],
					backgroundColor: backgroundColors,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: tooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				datasets: { pie: pieOptionsConfig }
			}
		};

		if (redemptionChart.current) redemptionChart.current.destroy();
		const ctx = document.getElementById('redemptionChart');
		if (ctx) redemptionChart.current = new Chart(ctx, pieConfig);
	};

	let programFundingChart = useRef(null);
	let doProgramFundingChart = () => {
		let data = programFundingData || [];
		let theLabels = [];
		let theData = [];
		data.forEach((item) => {
			const fid = item.fundingId || item.dataId;
			theLabels.push(getFundingName(fid) + ' ' + item.percentage + '%');
			theData.push(item.amount);
		});
		const chartTitle = ['Issuance Funding Source', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		const pieConfig = {
			type: 'pie',
			data: {
				labels: theLabels,
				datasets: [{
					data: theData,
					backgroundColor: backgroundColors,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: tooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				datasets: { pie: pieOptionsConfig }
			}
		};

		if (programFundingChart.current) programFundingChart.current.destroy();
		const ctx = document.getElementById('programFundingChart');
		if (ctx) programFundingChart.current = new Chart(ctx, pieConfig);
	};

	const barTooltipCallbacks = {
		label: (context) => {
			return context.dataset.label + ' ' + context.parsed.y.toFixed(2);
		}
	};

	let redemptionsChart = useRef(null);
	let doRedemptionsChart = () => {
		let data = programRedemptionData || [];
		let theLabels = [];
		let theData = [];

		if (dateRanges.dateRange1 === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item.trxDate);
				if (idx !== null) byMonth[idx] = item.amount || 0;
			});
			theLabels = LY_MONTHS;
			theData = byMonth;
		} else {
			data.forEach((item) => {
				theLabels.push(item.trxDate);
				theData.push(item.amount);
			});
		}

		let chartTitle = ['Redemption Amounts', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange1));

		const barConfig = {
			type: 'bar',
			data: {
				labels: theLabels,
				datasets: [{
					label: 'Redemption Amount',
					data: theData,
					backgroundColor: namedColors.green,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: barTooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Dollars' },
						suggestedMax: 10
					}
				}
			}
		};

		if (redemptionsChart.current) redemptionsChart.current.destroy();
		const ctx = document.getElementById('redemptionsChart');
		if (ctx) redemptionsChart.current = new Chart(ctx, barConfig);
	};

	let redemptionsCountChart = useRef(null);
	let doRedemptionsCountChart = () => {
		let data = programRedemptionCountData || [];
		let theLabels = [];
		let theData = [];

		if (dateRanges.dateRange2 === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item.trxDate);
				if (idx !== null) byMonth[idx] = item.count || 0;
			});
			theLabels = LY_MONTHS;
			theData = byMonth;
		} else {
			data.forEach((item) => {
				theLabels.push(item.trxDate);
				theData.push(item.count);
			});
		}

		let chartTitle = ['Redemption Counts', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange2));

		const barConfig = {
			type: 'bar',
			data: {
				labels: theLabels,
				datasets: [{
					label: 'Redemption Count',
					data: theData,
					backgroundColor: namedColors.purple,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: { title: { display: true, text: chartTitle } },
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Count' },
						ticks: { stepSize: 1 },
						suggestedMax: 5
					}
				}
			}
		};

		if (redemptionsCountChart.current) redemptionsCountChart.current.destroy();
		const ctx = document.getElementById('redemptionsCountChart');
		if (ctx) redemptionsCountChart.current = new Chart(ctx, barConfig);
	};

	let issuancesChart = useRef(null);
	let doIssuancesChart = () => {
		let data = programIssuanceData || [];
		let theLabels = [];
		let theData = [];

		if (dateRanges.dateRange3 === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item.trxDate);
				if (idx !== null) byMonth[idx] = Number(item.amount) || 0;
			});
			theLabels = LY_MONTHS;
			theData = byMonth;
		} else {
			data.forEach((item) => {
				theLabels.push(item.trxDate);
				theData.push(Number(item.amount) || 0);
			});
		}

		let chartTitle = ['Issuance Amounts', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange3));

		const barConfig = {
			type: 'bar',
			data: {
				labels: theLabels,
				datasets: [{
					label: 'Issuance Amount',
					data: theData,
					backgroundColor: namedColors.orange,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: barTooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Dollars' },
						suggestedMax: 10
					}
				}
			}
		};

		if (issuancesChart.current) issuancesChart.current.destroy();
		const ctx = document.getElementById('issuancesChart');
		if (ctx) issuancesChart.current = new Chart(ctx, barConfig);
	};

	let issuanceCountChart = useRef(null);
	let doIssuanceCountChart = () => {
		let data = programIssuanceCountData || [];
		let theLabels = [];
		let theData = [];

		if (dateRanges.dateRange7 === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item.trxDate);
				if (idx !== null) byMonth[idx] = item.count || 0;
			});
			theLabels = LY_MONTHS;
			theData = byMonth;
		} else {
			data.forEach((item) => {
				theLabels.push(item.trxDate);
				theData.push(item.count);
			});
		}

		let chartTitle = ['Issuance Counts', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange7));

		const barConfig = {
			type: 'bar',
			data: {
				labels: theLabels,
				datasets: [{
					label: 'Issuance Count',
					data: theData,
					backgroundColor: namedColors.red,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: { title: { display: true, text: chartTitle } },
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Count' },
						ticks: { stepSize: 1 },
						suggestedMax: 5
					}
				}
			}
		};

		if (issuanceCountChart.current) issuanceCountChart.current.destroy();
		const ctx = document.getElementById('issuanceCountChart');
		if (ctx) issuanceCountChart.current = new Chart(ctx, barConfig);
	};

	let recipientRedemptionsCountChart = useRef(null);
	let doRecipientRedemptionsCountChart = () => {
		let data = programRecipientRedemptionCountData || [];
		let theLabels = [];
		let theData = [];

		if (dateRanges.dateRange4 === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item.trxDate);
				if (idx !== null) byMonth[idx] = item.count || 0;
			});
			theLabels = LY_MONTHS;
			theData = byMonth;
		} else {
			data.forEach((item) => {
				theLabels.push(item.trxDate);
				theData.push(item.count);
			});
		}

		let chartTitle = ['Recipient Redemption Counts', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange4));

		const barConfig = {
			type: 'bar',
			data: {
				labels: theLabels,
				datasets: [{
					label: 'Recipient Count',
					data: theData,
					backgroundColor: namedColors.blue,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: { title: { display: true, text: chartTitle } },
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Count' },
						ticks: { stepSize: 1 },
						suggestedMax: 5
					}
				}
			}
		};

		if (recipientRedemptionsCountChart.current) recipientRedemptionsCountChart.current.destroy();
		const ctx = document.getElementById('recipientRedemptionsCountChart');
		if (ctx) recipientRedemptionsCountChart.current = new Chart(ctx, barConfig);
	};

	const tooltipCallbacksInt = {
		label: (context) => {
			return context.label + ': ' + context.parsed;
		}
	};

	let merchantParticipationChart = useRef(null);
	let doMerchantParticipationChart = () => {
		let theLabels = [
			'Redemption ' + merchantData.merchantRedemptionCount.percentage.toFixed(1) + '%',
			'No Redemption ' + merchantData.merchantNoRedemptionCount.percentage.toFixed(1) + '%'
		];
		let theData = [merchantData.merchantRedemptionCount.count, merchantData.merchantNoRedemptionCount.count];

		const chartTitle = ['Merchant Participation', currentProgramName(), getDateOptionText(dateRanges.dateRange5)];
		const pieConfig = {
			type: 'pie',
			data: {
				labels: theLabels,
				datasets: [{
					data: theData,
					backgroundColor: backgroundColors,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: tooltipCallbacksInt },
					title: { display: true, text: chartTitle }
				},
				datasets: { pie: pieOptionsConfig }
			}
		};

		if (merchantParticipationChart.current) merchantParticipationChart.current.destroy();
		const ctx = document.getElementById('merchantParticipationChart');
		if (ctx) merchantParticipationChart.current = new Chart(ctx, pieConfig);
	};

	let topMerchantRedemptionsChart = useRef(null);
	let doTopMerchantRedemptionsChart = () => {
		let data = topMerchantRedemptionsData || [];
		let theLabels = [''];
		let theData = [];
		data.forEach((item, index) => {
			theData.push({
				label: item.dataName,
				data: [item.amount],
				backgroundColor: backgroundColors[index]
			});
		});

		let chartTitle = ['Top Redemption Merchants', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange6));

		const barConfig = {
			type: 'bar',
			data: { labels: theLabels, datasets: theData },
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: barTooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Dollars' },
						suggestedMax: 10
					}
				}
			}
		};

		if (topMerchantRedemptionsChart.current) topMerchantRedemptionsChart.current.destroy();
		const ctx = document.getElementById('topMerchantRedemptionsChart');
		if (ctx) topMerchantRedemptionsChart.current = new Chart(ctx, barConfig);
	};

	let incentivesChart = useRef(null);
	let doIncentivesChart = () => {
		let data = programIncentiveData || [];
		let theLabels = [];
		let theData = [];

		if (dateRanges.dateRange8 === 'LY') {
			const byMonth = new Array(12).fill(0);
			data.forEach((item) => {
				const idx = monthIndexFromYYYYMMDD(item.trxDate);
				if (idx !== null) byMonth[idx] = Number(item.amount) || 0;
			});
			theLabels = LY_MONTHS;
			theData = byMonth;
		} else {
			data.forEach((item) => {
				theLabels.push(item.trxDate);
				theData.push(Number(item.amount) || 0);
			});
		}

		let chartTitle = ['Incentives Earned', (searchForm.agencyUuid ? currentAgencyName() : currentProgramName())];
		if (fundingName.current) chartTitle.push(fundingName.current);
		chartTitle.push(getDateOptionText(dateRanges.dateRange8));

		const barConfig = {
			type: 'bar',
			data: {
				labels: theLabels,
				datasets: [{
					label: 'Incentive Amount',
					data: theData,
					backgroundColor: namedColors.yellow,
				}]
			},
			options: {
				animation: animationConfig,
				plugins: {
					tooltip: { callbacks: barTooltipCallbacks },
					title: { display: true, text: chartTitle }
				},
				scales: {
					y: {
						beginAtZero: true,
						title: { display: true, text: 'Dollars' },
						suggestedMax: 10
					}
				}
			}
		};

		if (incentivesChart.current) incentivesChart.current.destroy();
		const ctx = document.getElementById('incentivesChart');
		if (ctx) incentivesChart.current = new Chart(ctx, barConfig);
	};

	// --- NEW: when switching to TABLE, destroy chart instance so it doesn't mess with canvas/layout ---
	useEffect(() => {
		if (viewMode.programFundingPie === 'table' && programFundingChart.current) programFundingChart.current.destroy();
	}, [viewMode.programFundingPie]);
	useEffect(() => {
		if (viewMode.issuancePie === 'table' && fundingChart.current) fundingChart.current.destroy();
	}, [viewMode.issuancePie]);
	useEffect(() => {
		if (viewMode.redemptionPie === 'table' && redemptionChart.current) redemptionChart.current.destroy();
	}, [viewMode.redemptionPie]);
	useEffect(() => {
		if (viewMode.redemptionsBar === 'table' && redemptionsChart.current) redemptionsChart.current.destroy();
	}, [viewMode.redemptionsBar]);
	useEffect(() => {
		if (viewMode.redemptionsCountBar === 'table' && redemptionsCountChart.current) redemptionsCountChart.current.destroy();
	}, [viewMode.redemptionsCountBar]);
	useEffect(() => {
		if (viewMode.recipientRedemptionsCountBar === 'table' && recipientRedemptionsCountChart.current) recipientRedemptionsCountChart.current.destroy();
	}, [viewMode.recipientRedemptionsCountBar]);
	useEffect(() => {
		if (viewMode.issuancesBar === 'table' && issuancesChart.current) issuancesChart.current.destroy();
	}, [viewMode.issuancesBar]);
	useEffect(() => {
		if (viewMode.issuanceCountBar === 'table' && issuanceCountChart.current) issuanceCountChart.current.destroy();
	}, [viewMode.issuanceCountBar]);
	useEffect(() => {
		if (viewMode.incentivesBar === 'table' && incentivesChart.current) incentivesChart.current.destroy();
	}, [viewMode.incentivesBar]);
	useEffect(() => {
		if (viewMode.merchantParticipationPie === 'table' && merchantParticipationChart.current) merchantParticipationChart.current.destroy();
	}, [viewMode.merchantParticipationPie]);
	useEffect(() => {
		if (viewMode.topMerchantsBar === 'table' && topMerchantRedemptionsChart.current) topMerchantRedemptionsChart.current.destroy();
	}, [viewMode.topMerchantsBar]);

	let canvasSize = { maxHeight: 280, maxWidth: 280, margin: 'auto' };
	let canvasSize2 = { height: 300, maxHeight: 300, width: 520, margin: 'auto' };

	const DateOptions = [
		{ id: "30", text: "Last 30 Days" },
		{ id: "MTD", text: "Month To Date" },
		{ id: "YTD", text: "Year To Date" },
		{ id: "LY", text: "Last Year" },
		{ id: "", text: "To Date" },
	];

	let getDateOptionText = (id) => {
		let text = DateOptions.find(item => item.id == id);
		return text ? text.text : '';
	};

	return (
		<>
			<h3>Dashboard</h3>
			<MessageTag message={message} />
			<Processing show={showSpinner} />

			<form onSubmit={handleSubmit}>
				<div className="row">
					<div className="form-group col-12 col-md-3">
						<label htmlFor="programUuid">Program</label>
						<select id="programUuid" className="form-control" value={searchForm.programUuid} onChange={handleFieldChange} >
							{selectOptions(programs, 'programUuid', 'programName')}
						</select>
					</div>
					<div className="form-group col-12 col-md-4">
						<label htmlFor="agencyUuid">Agency</label>
						<select id="agencyUuid" className="form-control" value={searchForm.agencyUuid} onChange={handleFieldChange} disabled={agencies.length < 2}>
							{selectOptions(agencies, 'agencyUuid', 'agencyName')}
						</select>
					</div>
					<div className="form-group col-12 col-md-3">
						<label htmlFor="fundingId">Funding Source</label>
						<select id="fundingId" className="form-control" value={searchForm.fundingId} onChange={handleFieldChange} >
							{selectOptions(fundingSources, 'fundingId', 'fundingName')}
						</select>
					</div>
					<div className="form-group col-12 col-md-2">
						<label htmlFor="dateRange">Date Range</label>
						<select id="dateRange" className="form-control" onChange={handleFieldChange} defaultValue={"YTD"}>
							{selectOptions(DateOptions)}
						</select>
					</div>
					<div className="form-group col-12 col-md-3">
						<br />
						<button className="btn btn-primary" id="btnSearch" disabled={searchForm.programUuid ? false : true}>Retrieve Data</button>
					</div>
				</div>
			</form>

			<div className="row">

				{/* Funding Source Pie */}
				<div className="col-12 col-lg-4 outline" style={{ display: displayData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ marginBottom: 6 }}>
						<ToggleButtons modeKey="programFundingPie" />
					</div>
					{viewMode.programFundingPie === 'chart' ? (
						<div><canvas id="programFundingChart" style={canvasSize}></canvas></div>
					) : null}
					<DisplayAllocation />
				</div>

				{/* Issuance Pie */}
				<div className="col-12 col-lg-4 outline" style={{ display: displayData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ marginBottom: 6 }}>
						<ToggleButtons modeKey="issuancePie" />
					</div>
					{viewMode.issuancePie === 'chart' ? (
						<div><canvas id="fundingChart" style={canvasSize}></canvas></div>
					) : null}
					<DisplayIssuance />
				</div>

				{/* Redemption Pie */}
				<div className="col-12 col-lg-4 outline" style={{ display: displayData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ marginBottom: 6 }}>
						<ToggleButtons modeKey="redemptionPie" />
					</div>
					{viewMode.redemptionPie === 'chart' ? (
						<div><canvas id="redemptionChart" style={canvasSize}></canvas></div>
					) : null}
					<DisplayRedemption />
				</div>

				{/* Redemption Amounts */}
				<div className="col-12 col-lg-6 outline" style={{ display: programRedemptionData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="redemptionsBar" />
						<select id="dateRange1" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange1}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.redemptionsBar === 'chart' ? (
						<div><canvas id="redemptionsChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTrxAmount data={programRedemptionData} dateRangeId={dateRanges.dateRange1} labelAmount="Amount" />
					)}
				</div>

				{/* Redemption Counts */}
				<div className="col-12 col-lg-6 outline" style={{ display: programRedemptionCountData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="redemptionsCountBar" />

						<select id="dateRange2" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange2}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.redemptionsCountBar === 'chart' ? (
						<div><canvas id="redemptionsCountChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTrxCount data={programRedemptionCountData} dateRangeId={dateRanges.dateRange2} labelCount="Count" />
					)}
				</div>

				{/* Recipient Redemption Counts */}
				<div className="col-12 col-lg-6 outline" style={{ display: programRecipientRedemptionCountData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="recipientRedemptionsCountBar" />

						<select id="dateRange4" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange4}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.recipientRedemptionsCountBar === 'chart' ? (
						<div><canvas id="recipientRedemptionsCountChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTrxCount data={programRecipientRedemptionCountData} dateRangeId={dateRanges.dateRange4} labelCount="Count" />
					)}
				</div>

				{/* Issuance Amounts */}
				<div className="col-12 col-lg-6 outline" style={{ display: programIssuanceData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="issuancesBar" />

						<select id="dateRange3" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange3}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.issuancesBar === 'chart' ? (
						<div><canvas id="issuancesChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTrxAmount data={programIssuanceData} dateRangeId={dateRanges.dateRange3} labelAmount="Amount" />
					)}
				</div>

				{/* Issuance Counts */}
				<div className="col-12 col-lg-6 outline" style={{ display: programIssuanceCountData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="issuanceCountBar" />

						<select id="dateRange7" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange7}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.issuanceCountBar === 'chart' ? (
						<div><canvas id="issuanceCountChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTrxCount data={programIssuanceCountData} dateRangeId={dateRanges.dateRange7} labelCount="Count" />
					)}
				</div>

				{/* Incentives */}
				<div className="col-12 col-lg-6 outline" style={{ display: programIncentiveData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="incentivesBar" />

						<select id="dateRange8" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange8}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.incentivesBar === 'chart' ? (
						<div><canvas id="incentivesChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTrxAmount data={programIncentiveData} dateRangeId={dateRanges.dateRange8} labelAmount="Amount" />
					)}
				</div>

				{/* Merchant Participation */}
				<div className="col-12 col-lg-6 outline" style={{ display: merchantData && merchantData.merchantCount && merchantData.merchantCount.count ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="merchantParticipationPie" />

						<select id="dateRange5" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange5}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.merchantParticipationPie === 'chart' ? (
						<div><canvas id="merchantParticipationChart" style={canvasSize2}></canvas></div>
					) : null}
					<DisplayMerchantParticipation />
				</div>

				{/* Top Merchants */}
				<div className="col-12 col-lg-6 outline" style={{ display: topMerchantRedemptionsData ? 'block' : 'none' }}>
					<div className="pull-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
						<ToggleButtons modeKey="topMerchantsBar" />

						<select id="dateRange6" className="form-control micro" onChange={handleDateRangeChange} value={dateRanges.dateRange6}>
							{selectOptions(DateOptions)}
						</select>
					</div>

					{viewMode.topMerchantsBar === 'chart' ? (
						<div><canvas id="topMerchantRedemptionsChart" style={canvasSize2}></canvas></div>
					) : (
						<TableTopMerchants data={topMerchantRedemptionsData} />
					)}
				</div>

			</div>
		</>
	);
}